/**
 * How an `events` row is spoken aloud in the activity feed.
 *
 * The verbs are the API's registry (`apps/api/src/modules/events/event-verbs.ts`);
 * only the COUNTRY-SCOPED ones can appear here, because
 * `GET /countries/:cc/events` filters `WHERE country_id = :id`, which structurally
 * cannot match the global (`country_id IS NULL`) rows. `country.updated`,
 * `fx.rates_updated` and `agent.auth_failed` are global and are therefore absent
 * by contract rather than by oversight.
 *
 * ⚠ CORRECTED: `product.*` USED TO BE IN THAT LIST AND IS NOT GLOBAL. Migration 22 gave
 * `products` a `country_id`, and `products.service.ts` was changed to emit
 * `countryId: Number(saved.country_id)` — its own comment says so ("⚠ THIS WAS `null` …
 * It has one since migration 22, so the event carries it"). So `product.created` and
 * `product.updated` DO land on this feed, with no builder below, and `presentEvent` falls
 * through to `humanise(verb)` — they render as a bare "Created" / "Updated". That is a
 * real gap in this file rather than an absence by contract; it is FILED rather than fixed
 * here, because writing two new builders is a product change and this correction is not.
 *
 * ⚠ `events.redacted` WAS A THIRD INSTANCE OF THAT SAME GAP, INTRODUCED BY THE COMMIT THAT
 * WROTE THE CORRECTION ABOVE, AND IT IS FIXED RATHER THAN FILED. The difference is what the
 * event is FOR: the scrub script has no `down()`, so its per-country marker is the entire
 * substitute for reversibility. Rendering it as the bare word "Redacted" under a success
 * checkmark — no count, no key names, no id range — made the one event that exists to be
 * read the least legible on the feed. There are 7 such rows live across two databases.
 *
 * ⚠ AND THE CLAIM IS TRUE OF THE REST FETCH ONLY. `RecentActivityWidget` merges this
 * feed's history with `useEventStream`, and that hook applies NO scope filter of its own —
 * "`countryCode` is not a filter this hook applies — the TICKET carries the scope". A
 * global frame the ticket admits IS rendered live, by these same builders.
 *
 * ===========================================================================
 * WHY THE FIGMA GLYPHS WERE REPLACED — AND WHY ONE OF THEM WAS A LATENT DEFECT
 * ===========================================================================
 * `Widget/ActivityRow` (node 6:408) puts a TEXT GLYPH in a 28px tinted circle:
 * ✓ for a quote sent, + for a new inquiry, ↗ for a stage move, ✎ for a follow-up
 * — and **₹ for "Advance received from Mad Plus"**.
 *
 * That last one cannot ship. This app is five countries and the two pilots are
 * ANGOLA (AOA) and DR CONGO (CDF); a rupee sign hardcoded into the activity feed
 * would print ₹ next to a Kwanza transaction. Since one glyph had to change
 * anyway, all of them are mapped to lucide icons — consistent with the nav mapping
 * forced by the same design's empty-square icon defect, and consistent with
 * lucide-react being the declared icon source. Recorded in PHASE2-REPORT.
 *
 * ===========================================================================
 * THE SENTENCES USE THE PAYLOAD THE API ACTUALLY EMITS
 * ===========================================================================
 * Figma writes "New inquiry from Om Traders" — a PARTY NAME. The `inquiry.created`
 * payload carries `{country, doc_no, party_id, subject, assigned_to, currency}`; it
 * does not carry the party's NAME, and the feed endpoint does not join parties. So the
 * sentences are built from `doc_no` (which is the thing a coordinator quotes on the
 * phone) plus `subject` when present. Inventing a customer name — or fetching one
 * per row — would be worse than naming the document.
 *
 * ⚠ THAT SENTENCE WAS FALSE FOR TWO PHASES, AND NOTHING ANYWHERE TESTED IT. The payload
 * DID carry `party_name`: it was declared `OPERATOR_TEXT` in the API's `payload-keys.ts`
 * and emitted by `inquiries.service.ts` and `quotes.service.ts`. So the customer's name
 * rode every SSE frame and landed in every `events.payload` row while this file explained
 * at length why it was not there — and no builder below ever read it, which is why nobody
 * noticed. The claim is true NOW, by owner ruling 3, and it is held by a test rather than
 * by this comment: `apps/api/src/modules/inquiries/inquiries.spec.ts` and
 * `apps/api/src/modules/quotes/quotes.spec.ts` each assert the key absent, tied to a row
 * in `apps/api/test/channel-manifest.spec.ts` that requires them by name.
 *
 * The API-side removal is what makes this paragraph honest; the design decision it
 * describes never changed. Note the narrow scope: no builder in THIS file reads
 * `party_name`, but the CRF and inquiry RECORD pages do read it off their REST payloads,
 * which are a different surface and still carry it.
 *
 * An UNKNOWN verb is humanised rather than dropped: Phases 3–5 add verbs this file
 * has never heard of, and a feed that silently swallows them would hide real
 * activity. `quote.confirmed` renders as "Quote confirmed".
 */

import {
  ArrowRightLeft,
  BadgeCheck,
  Ban,
  Building2,
  CircleDot,
  FileCog,
  Hash,
  HeartPulse,
  KeyRound,
  Link2,
  Package,
  PencilLine,
  Plus,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  TriangleAlert,
  UserCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { STAGES, isInquiryStage } from './stages';

/** Which `status/*` pair tints the icon bubble. */
export type EventTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export interface EventRow {
  id: string;
  country_id: number | null;
  entity_type: string;
  entity_id: string | null;
  verb: string;
  actor_id: string | null;
  payload: Record<string, unknown> | null;
  /** ISO-8601 UTC. */
  occurred_at: string;
}

export interface EventPresentation {
  Icon: LucideIcon;
  tone: EventTone;
  /** One line, present tense of a past fact — the sentence in the feed. */
  text: string;
}

function str(payload: Record<string, unknown> | null, key: string): string | null {
  if (payload === null) return null;
  const value = payload[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function num(payload: Record<string, unknown> | null, key: string): number | null {
  if (payload === null) return null;
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** `need_quote` → "Need Quote", via the ONE stage map. Unknown values pass through. */
function stageLabel(value: string | null): string {
  if (value === null) return 'another stage';
  return isInquiryStage(value) ? STAGES[value].label : value;
}

/** `parties.quarantined` → "Parties quarantined". The honest fallback. */
function humanise(verb: string): string {
  const tail = verb.includes('.') ? verb.slice(verb.indexOf('.') + 1) : verb;
  const words = tail.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

type Builder = (row: EventRow) => EventPresentation;

const BUILDERS: Record<string, Builder> = {
  // ── inquiries (Phase 2) ──────────────────────────────────────────────────
  'inquiry.created': (row) => {
    const doc = str(row.payload, 'doc_no');
    const subject = str(row.payload, 'subject');
    return {
      Icon: Plus,
      tone: 'info',
      text:
        subject === null
          ? `New inquiry ${doc ?? 'created'}`
          : `New inquiry ${doc ?? ''} — ${subject}`.trim(),
    };
  },
  'inquiry.updated': (row) => ({
    Icon: PencilLine,
    tone: 'neutral',
    text: `${str(row.payload, 'doc_no') ?? 'An inquiry'} was edited`,
  }),
  'inquiry.stage_changed': (row) => {
    const doc = str(row.payload, 'doc_no') ?? 'An inquiry';
    const to = stageLabel(str(row.payload, 'to'));
    const reason = str(row.payload, 'reason');
    return {
      Icon: ArrowRightLeft,
      tone: str(row.payload, 'to') === 'lost' ? 'danger' : 'info',
      text: reason === null ? `${doc} moved to ${to}` : `${doc} moved to ${to} — ${reason}`,
    };
  },
  'inquiry.assigned': (row) => ({
    Icon: UserCheck,
    tone: 'neutral',
    text: `${str(row.payload, 'doc_no') ?? 'An inquiry'} was reassigned`,
  }),
  'inquiry.bulk_updated': (row) => {
    const action = str(row.payload, 'action');
    const updated = num(row.payload, 'updated');
    const count = updated === null ? 'Several' : String(updated);
    return {
      Icon: Hash,
      tone: 'neutral',
      text:
        action === 'stage'
          ? `${count} inquiries moved stage in bulk`
          : `${count} inquiries reassigned in bulk`,
    };
  },
  'followup.created': (row) => {
    const doc = str(row.payload, 'doc_no') ?? 'an inquiry';
    const extra = str(row.payload, 'kind') === 'extra_call';
    return {
      Icon: extra ? CircleDot : PencilLine,
      tone: extra ? 'warning' : 'neutral',
      text: extra ? `Extra call logged on ${doc}` : `Follow-up added on ${doc}`,
    };
  },

  // ── parties (Phase 1) ────────────────────────────────────────────────────
  'party.created': () => ({ Icon: Building2, tone: 'success', text: 'New prospect created' }),
  'party.linked': () => ({ Icon: Link2, tone: 'success', text: 'A party was linked to Tally' }),
  'parties.synced': (row) => {
    const upserted = num(row.payload, 'upserted') ?? num(row.payload, 'count');
    return {
      Icon: RefreshCw,
      tone: 'neutral',
      text:
        upserted === null
          ? 'Parties synced from Tally'
          : `${String(upserted)} parties synced from Tally`,
    };
  },
  'parties.quarantined': (row) => {
    const count = num(row.payload, 'count') ?? num(row.payload, 'quarantined');
    return {
      Icon: TriangleAlert,
      tone: 'warning',
      text:
        count === null
          ? 'Some Tally party rows were quarantined'
          : `${String(count)} Tally party rows were quarantined`,
    };
  },

  // ── agent (Phase 1) ──────────────────────────────────────────────────────
  'agent.heartbeat': (row) => {
    const status = str(row.payload, 'status');
    return {
      Icon: HeartPulse,
      tone: status === 'down' ? 'danger' : 'success',
      text: status === 'down' ? 'Tally agent went offline' : 'Tally agent is online',
    };
  },
  'agent_key.created': () => ({ Icon: KeyRound, tone: 'neutral', text: 'An agent key was issued' }),
  'agent_key.revoked': () => ({ Icon: Ban, tone: 'warning', text: 'An agent key was revoked' }),

  // ── network rules (Phase 2.5b · D7) ──────────────────────────────────────
  // Country-scoped, so they belong in this feed by the file-header contract.
  // The payload carries `{country, cidr, label}` — the LABEL is the sentence's
  // noun because that is the name the admin gave the premises; the CIDR stays
  // out of the feed line (it is one click away on the Network rules screen,
  // and a feed sentence is read by more roles than the config screen is).
  // The GLOBAL `premises.denied` verb is deliberately absent: country feeds
  // structurally cannot contain `country_id IS NULL` rows.
  'network_rule.created': (row) => ({
    Icon: ShieldCheck,
    tone: 'neutral',
    text: `Network '${str(row.payload, 'label') ?? 'unnamed'}' now admits users`,
  }),
  'network_rule.updated': (row) => ({
    Icon: ShieldCheck,
    tone: 'neutral',
    text: `Network rule '${str(row.payload, 'label') ?? 'unnamed'}' was changed`,
  }),
  'network_rule.removed': (row) => ({
    Icon: ShieldOff,
    tone: 'warning',
    text: `Network '${str(row.payload, 'label') ?? 'unnamed'}' was deactivated`,
  }),

  // ── config (Phase 0) ─────────────────────────────────────────────────────
  'tax_rule.created': () => ({ Icon: FileCog, tone: 'neutral', text: 'A tax rule was added' }),
  'tax_rule.updated': () => ({ Icon: FileCog, tone: 'neutral', text: 'A tax rule was changed' }),
  'report_config.updated': () => ({
    Icon: FileCog,
    tone: 'neutral',
    text: 'Report configuration changed',
  }),
  'series.corrected': () => ({
    Icon: Hash,
    tone: 'warning',
    text: 'A numbering series was corrected',
  }),
  'series.test_issued': () => ({
    Icon: Hash,
    tone: 'neutral',
    text: 'A test document number was issued',
  }),
  // Design §3 U1 — the irreversible AGT cut-over: the série it replaced is closed for good.
  'series.opened': (row) => ({
    Icon: Hash,
    tone: 'warning',
    text: `AGT série ${str(row.payload, 'serie_code') ?? 'unnamed'} was opened; the series it replaces is closed`,
  }),
  'country_products.updated': () => ({
    Icon: Package,
    tone: 'neutral',
    text: 'The product list for this country changed',
  }),

  /*
   * ⚠ THE ONE EVENT WHOSE WHOLE PURPOSE IS TO BE READ, AND IT HAD NO BUILDER.
   *
   * `scrub-events-money.ts` argues in its own header that the redaction marker must be
   * emitted PER COUNTRY because the country feed is where the edited rows are read — "a
   * Country Manager whose history silently lost a field would have no marker anywhere".
   * With no builder here, `presentEvent` fell through to `humanise('events.redacted')` and
   * that marker rendered as the single word "Redacted" — under `BadgeCheck`, a SUCCESS
   * glyph — carrying none of the counts, key names or id range the script deliberately
   * captured. The audit trail is the only substitute for a `down()` that cannot exist, and
   * it was illegible.
   *
   * `warning`, not `neutral` and certainly not a checkmark: history was destroyed here.
   */
  'events.redacted': (row) => {
    const rows = num(row.payload, 'rows');
    const keys = Array.isArray(row.payload?.keys)
      ? (row.payload.keys as unknown[]).filter(
          (key): key is string => typeof key === 'string',
        )
      : [];
    const what = keys.length === 0 ? 'a field' : keys.join(' and ');
    const many = rows === null ? 'Some' : String(rows);
    const noun = rows === 1 ? 'event' : 'events';
    return {
      Icon: Ban,
      tone: 'warning',
      text: `${many} past ${noun} had ${what} removed from the record`,
    };
  },
};

export function presentEvent(row: EventRow): EventPresentation {
  const builder = BUILDERS[row.verb];
  if (builder !== undefined) return builder(row);
  // Unknown verb — almost certainly a later phase's. Rendered honestly.
  return { Icon: BadgeCheck, tone: 'neutral', text: humanise(row.verb) };
}

const TONE_CLASSES: Record<EventTone, string> = {
  info: 'bg-info-bg text-info-fg',
  success: 'bg-ok-bg text-ok-fg',
  warning: 'bg-warn-bg text-warn-fg',
  danger: 'bg-danger-bg text-danger-fg',
  // No `status/*` pair means neutral: the design's own subtle chip treatment.
  neutral: 'bg-subtle text-fg-secondary',
};

export function eventToneClasses(tone: EventTone): string {
  return TONE_CLASSES[tone];
}

/**
 * "2 hours ago" / "Yesterday" / "2 days ago" — the exact vocabulary
 * `Widget/RecentActivity` uses (nodes 6:413, 12:4063, 12:4075).
 *
 * Deliberately not `Intl.RelativeTimeFormat`: it has no "Yesterday", and matching
 * the design's wording matters more than i18n in a phase where i18n is explicitly
 * out of scope.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const ms = then.getTime();
  if (!Number.isFinite(ms)) return '';
  const seconds = Math.floor((now.getTime() - ms) / 1000);
  if (seconds < 45) return 'just now';
  if (seconds < 90) return 'a minute ago';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${String(days)} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${String(months)} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${String(years)} year${years === 1 ? '' : 's'} ago`;
}
